import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Tag } from "lucide-react";
import { useEffect, useState } from "react";
import { publicApi } from "../lib/api";
import { SITE } from "../data/constants";
import SEO from "../components/SEO";
import PageSeoOverride from "../components/PageSeoOverride";
import Picture from "../components/Picture";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi
      .listPosts()
      .then(setPosts)
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO
        title="Blog & Artikel BYD Cibubur - Panduan, Review & Tips Mobil Listrik"
        description="Baca panduan charging BYD di rumah, garansi baterai, perbandingan model, dan tips perawatan dari Senior Sales Consultant BYD Haka Auto Cibubur."
        canonical={`${SITE.domain}/blog`}
      />
      <PageSeoOverride pageKey="blog" />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-12">
        <p className="text-xs uppercase tracking-[0.25em] text-[#6E6E73] mb-5 fade-up">
          Wawasan & Panduan
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#0F0F0F] leading-[1.05] max-w-3xl fade-up fade-up-delay-1">
          Blog BYD <br />
          <span className="italic font-light">Cibubur.</span>
        </h1>
        <p className="mt-6 text-base text-[#6E6E73] max-w-xl leading-relaxed fade-up fade-up-delay-2">
          Panduan ownership, review test drive, dan analisis biaya operasional —
          ditulis berdasarkan pengalaman langsung di lapangan.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24 lg:pb-32">
        {loading ? (
          <p className="text-sm text-[#86868B]">Memuat artikel…</p>
        ) : posts.length === 0 ? (
          <p className="text-sm text-[#86868B]">Belum ada artikel dipublish.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p, i) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className={`group rounded-3xl border border-[#E5E5EA] overflow-hidden bg-[#F5F5F7] hover:border-[#0F0F0F]/40 transition-colors ${
                  i === 0 ? "md:col-span-2 lg:col-span-2" : ""
                }`}
                data-testid={`blog-card-${p.slug}`}
              >
                <div
                  className={`bg-white overflow-hidden ${
                    i === 0 ? "aspect-[16/9]" : "aspect-[4/3]"
                  }`}
                >
                  <Picture
                    src={p.cover_image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1400ms] ease-out"
                    width={i === 0 ? 1600 : 800}
                  />
                </div>
                <div className="p-7 lg:p-8">
                  <div className="flex items-center gap-3 text-xs text-[#86868B] mb-3">
                    <span className="inline-flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5" />
                      {p.category}
                    </span>
                    <span>•</span>
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(p.created_at).toLocaleDateString("id-ID", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <h2
                      className={`font-medium tracking-tight text-[#0F0F0F] leading-tight ${
                        i === 0 ? "text-2xl lg:text-3xl" : "text-xl"
                      }`}
                    >
                      {p.title}
                    </h2>
                    <ArrowUpRight className="w-5 h-5 text-[#0F0F0F] shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </div>
                  <p className="mt-3 text-sm text-[#6E6E73] leading-relaxed line-clamp-3">
                    {p.excerpt}
                  </p>
                  <p className="mt-5 text-xs text-[#86868B]">Oleh {p.author}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
